import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Button, Chip, Popover, Tooltip, Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { capitalizar, CATEGORIAS_ORDEN } from '../../utils/UtilesComun';

/**
 * Ordena los grupos de una división de forma natural (ej. Grupo 1, Grupo 2, Grupo 10).
 */
function ordenarGrupos(ligas) {
  return [...ligas].sort((a, b) => {
    const grupoA = a.grupo || '';
    const grupoB = b.grupo || '';
    return grupoA.localeCompare(grupoB, undefined, { numeric: true, sensitivity: 'base' });
  });
}

/**
 * Componente que muestra las ligas agrupadas por categorías y divisiones
 * con diseño moderno y menú emergente anclado para seleccionar grupos.
 */
const TodasLigas = ({ ligas = [], filtroTexto = '', categoriaSeleccionada = 'TODAS' }) => {
  const navigate = useNavigate();

  // Estado para el popover desplegable de grupos
  const [anchorEl, setAnchorEl] = useState(null);
  const [popoverData, setPopoverData] = useState(null);

  const handleOpenPopover = (event, categoria, division, listaLigas) => {
    if (listaLigas.length === 1) {
      navigate(`/liga/${listaLigas[0].idLiga}`);
      return;
    }
    setAnchorEl(event.currentTarget);
    setPopoverData({
      categoria,
      division,
      ligas: ordenarGrupos(listaLigas),
    });
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    setPopoverData(null);
  };

  // Filtrado y agrupación en memoria
  const { categoriasOrdenadas, ligasPorCategoria } = useMemo(() => {
    const texto = filtroTexto.trim().toLowerCase();

    // 1. Filtrar ligas por texto y por categoría si aplica
    const filtradas = ligas.filter((liga) => {
      const matchCat =
        categoriaSeleccionada === 'TODAS' ||
        (liga.categoria && liga.categoria.toUpperCase() === categoriaSeleccionada.toUpperCase());

      if (!matchCat) {
        return false;
      }

      if (!texto) {
        return true;
      }

      const matchDivision = liga.division && liga.division.toLowerCase().includes(texto);
      const matchGrupo = liga.grupo && liga.grupo.toLowerCase().includes(texto);
      const matchCategoria = liga.categoria && liga.categoria.toLowerCase().includes(texto);
      return matchDivision || matchGrupo || matchCategoria;
    });

    // 2. Agrupar por categoría y división
    const porCategoria = filtradas.reduce((acc, liga) => {
      const categoria = liga.categoria?.toUpperCase() || 'OTROS';
      if (!acc[categoria]) {
        acc[categoria] = {};
      }
      const division = liga.division || 'Sin División';
      if (!acc[categoria][division]) {
        acc[categoria][division] = [];
      }
      acc[categoria][division].push(liga);
      return acc;
    }, {});

    // 3. Ordenar categorías según CATEGORIAS_ORDEN
    const ordenadas = CATEGORIAS_ORDEN.filter((cat) => porCategoria[cat]);
    Object.keys(porCategoria).forEach((cat) => {
      if (!CATEGORIAS_ORDEN.includes(cat)) {
        ordenadas.push(cat);
      }
    });

    return { categoriasOrdenadas: ordenadas, ligasPorCategoria: porCategoria };
  }, [ligas, filtroTexto, categoriaSeleccionada]);

  if (!ligas || ligas.length === 0) {
    return (
      <Box
        sx={{
          p: 4,
          textAlign: 'center',
          backgroundColor: '#ffffff',
          borderRadius: '10px',
          border: '1px solid #e2e8f0',
        }}
      >
        <Typography sx={{ color: 'text.secondary', fontSize: '0.90rem' }}>
          No hay ligas disponibles para la temporada seleccionada.
        </Typography>
      </Box>
    );
  }

  if (categoriasOrdenadas.length === 0) {
    return (
      <Box
        sx={{
          p: 4,
          textAlign: 'center',
          backgroundColor: '#ffffff',
          borderRadius: '10px',
          border: '1px solid #e2e8f0',
        }}
      >
        <Typography sx={{ color: 'text.secondary', fontSize: '0.90rem' }}>
          No se encontraron competiciones que coincidan con los filtros aplicados.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      {categoriasOrdenadas.map((categoria) => {
        const divisionesMap = ligasPorCategoria[categoria] || {};
        const divisionesKeys = Object.keys(divisionesMap);

        // Conteo total de ligas en esta categoría
        const totalLigasCategoria = divisionesKeys.reduce((total, div) => total + divisionesMap[div].length, 0);

        return (
          <Box
            key={categoria}
            sx={{
              mb: 3,
              backgroundColor: '#ffffff',
              borderRadius: '12px',
              border: '1px solid #e2e8f0',
              boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
              overflow: 'hidden',
            }}
          >
            {/* Cabecera de la categoría */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                px: 2.5,
                py: 1.5,
                backgroundColor: '#f8fafc',
                borderBottom: '1px solid #e2e8f0',
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '0.95rem',
                  color: 'text.primary',
                  letterSpacing: '0.01em',
                }}
              >
                {capitalizar(categoria)}
              </Typography>

              <Chip
                label={`${divisionesKeys.length} ${divisionesKeys.length === 1 ? 'división' : 'divisiones'} · ${totalLigasCategoria} ${totalLigasCategoria === 1 ? 'grupo' : 'grupos'}`}
                size="small"
                sx={{
                  height: 24,
                  fontSize: '0.72rem',
                  fontWeight: 600,
                  backgroundColor: '#ffffff',
                  color: '#475569',
                  border: '1px solid #cbd5e1',
                }}
              />
            </Box>

            {/* Contenedor de divisiones */}
            <Box
              sx={{
                p: 2,
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1.5,
              }}
            >
              {divisionesKeys.map((division) => {
                const listaLigas = divisionesMap[division];
                const numGrupos = listaLigas.length;
                const tieneUnGrupo = numGrupos === 1;

                const isPopoverActive =
                  popoverData?.categoria === categoria && popoverData?.division === division && Boolean(anchorEl);

                return (
                  <Tooltip
                    key={division}
                    title={tieneUnGrupo ? 'Ir directamente a la liga' : 'Haz clic para seleccionar grupo'}
                    arrow
                    placement="top"
                  >
                    <Box
                      onClick={(e) => handleOpenPopover(e, categoria, division, listaLigas)}
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 1,
                        height: 40,
                        px: 1.75,
                        backgroundColor: isPopoverActive ? '#f0fdfa' : '#f8fafc',
                        border: '1px solid',
                        borderColor: isPopoverActive ? '#0f766e' : '#cbd5e1',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        userSelect: 'none',
                        transition: 'all 0.15s ease-in-out',
                        boxShadow: '0 1px 2px rgba(0, 0, 0, 0.04)',
                        '&:hover': {
                          borderColor: '#0f766e',
                          backgroundColor: '#f0fdfa',
                          boxShadow: '0 3px 8px rgba(15, 118, 110, 0.14)',
                          transform: 'translateY(-1px)',
                        },
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '0.82rem',
                          fontWeight: 600,
                          color: isPopoverActive ? '#0f766e' : '#0f172a',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {capitalizar(division)}
                      </Typography>

                      {/* Badge con el número de grupos */}
                      <Box
                        sx={{
                          px: 0.8,
                          py: 0.2,
                          borderRadius: '10px',
                          fontSize: '0.70rem',
                          fontWeight: 700,
                          backgroundColor: isPopoverActive ? '#0f766e' : '#e2e8f0',
                          color: isPopoverActive ? '#ffffff' : '#334155',
                          border: isPopoverActive ? '1px solid #0f766e' : '1px solid #cbd5e1',
                          transition: 'all 0.15s ease-in-out',
                        }}
                      >
                        {numGrupos}
                      </Box>

                      {/* Indicador de acción */}
                      {tieneUnGrupo ? (
                        <ChevronRightIcon
                          sx={{ fontSize: '1rem', color: isPopoverActive ? '#0f766e' : '#64748b', ml: -0.25 }}
                        />
                      ) : (
                        <ExpandMoreIcon
                          sx={{
                            fontSize: '1.05rem',
                            color: isPopoverActive ? '#0f766e' : '#64748b',
                            transform: isPopoverActive ? 'rotate(180deg)' : 'none',
                            transition: 'transform 0.2s ease-in-out',
                            ml: -0.25,
                          }}
                        />
                      )}
                    </Box>
                  </Tooltip>
                );
              })}
            </Box>
          </Box>
        );
      })}

      {/* Popover anclado para seleccionar el grupo de una división */}
      <Popover
        open={Boolean(anchorEl && popoverData)}
        anchorEl={anchorEl}
        onClose={handleClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            mt: 0.75,
            p: 2,
            maxWidth: 420,
            minWidth: 260,
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            boxShadow: '0 10px 30px rgba(0, 0, 0, 0.12)',
          },
        }}
      >
        {popoverData && (
          <Box>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1.5,
                pb: 1,
                borderBottom: '1px solid #f1f5f9',
              }}
            >
              <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', color: 'text.primary' }}>
                {capitalizar(popoverData.division)}
              </Typography>
              <Chip
                label={`${popoverData.ligas.length} grupos`}
                size="small"
                sx={{
                  height: 20,
                  fontSize: '0.68rem',
                  fontWeight: 600,
                  backgroundColor: '#f0fdfa',
                  color: '#0f766e',
                }}
              />
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: popoverData.ligas.length > 8 ? 'repeat(3, 1fr)' : 'repeat(2, 1fr)',
                gap: 1,
                maxHeight: 280,
                overflowY: 'auto',
                pr: 0.5,
              }}
            >
              {popoverData.ligas.map((liga) => (
                <Button
                  key={liga.idLiga}
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    handleClosePopover();
                    navigate(`/liga/${liga.idLiga}`);
                  }}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.76rem',
                    fontWeight: 600,
                    borderColor: '#e2e8f0',
                    color: '#334155',
                    backgroundColor: '#f8fafc',
                    py: 0.6,
                    px: 1,
                    borderRadius: '6px',
                    justifyContent: 'center',
                    '&:hover': {
                      backgroundColor: '#0f766e',
                      borderColor: '#0f766e',
                      color: '#ffffff',
                    },
                  }}
                >
                  {capitalizar(liga.grupo)}
                </Button>
              ))}
            </Box>
          </Box>
        )}
      </Popover>
    </>
  );
};

TodasLigas.propTypes = {
  ligas: PropTypes.arrayOf(
    PropTypes.shape({
      idLiga: PropTypes.number.isRequired,
      categoria: PropTypes.string,
      division: PropTypes.string,
      grupo: PropTypes.string,
    })
  ).isRequired,
  filtroTexto: PropTypes.string,
  categoriaSeleccionada: PropTypes.string,
};

export default TodasLigas;
